import Cookies from 'universal-cookie';
import { REFRESH_URL } from '../config';
import { markSessionExpired } from './authSession';

const cookies = new Cookies();

// Single shared promise prevents parallel refresh races
let refreshPromise = null;

const DEFAULT_EXPIRES_IN_SEC = 3600;

function clearAuthTokens() {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiresAt');
    cookies.remove('token', { path: '/' });
}

function storeAccessToken(accessToken, refreshToken, expiresIn) {
    const ttl = Number(expiresIn) > 0 ? Number(expiresIn) : DEFAULT_EXPIRES_IN_SEC;
    localStorage.setItem('token', accessToken);
    if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
    }
    localStorage.setItem('tokenExpiresAt', String(Date.now() + ttl * 1000));
    cookies.set('token', accessToken, { path: '/', maxAge: ttl });
}

async function refreshSession() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
        clearAuthTokens();
        markSessionExpired();
        throw new Error('No refresh token');
    }

    if (!REFRESH_URL) {
        clearAuthTokens();
        markSessionExpired();
        throw new Error('Refresh URL not configured');
    }

    let res;
    try {
        res = await fetch(REFRESH_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
        });
    } catch (err) {
        // Network blip — keep the session; caller can retry on the next request
        throw err;
    }

    if (!res.ok) {
        // Only wipe the session on definitive auth failure. Transient 5xx should not log the user out.
        if (res.status === 401 || res.status === 403) {
            clearAuthTokens();
            markSessionExpired();
        }
        throw new Error('Refresh failed');
    }

    const data = await res.json();
    storeAccessToken(data.accessToken, data.refreshToken, data.expiresIn);
    // Do not fire app:tokenChanged — silent refresh must not re-initialize tenants
}

export async function fetchWithAuth(url, options = {}) {
    const isFormData = options.body instanceof FormData;

    const buildHeaders = () => {
        const token = localStorage.getItem('token') || cookies.get('token');
        const headers = {
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            ...options.headers,
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        };
        if (isFormData) delete headers['Content-Type'];
        return headers;
    };

    // Proactive refresh: silently renew if access token expires within 5 minutes
    const expiresAt = parseInt(localStorage.getItem('tokenExpiresAt') || '0', 10);
    if (expiresAt && !Number.isNaN(expiresAt) && expiresAt - Date.now() < 5 * 60 * 1000) {
        if (!refreshPromise) {
            refreshPromise = refreshSession().finally(() => { refreshPromise = null; });
        }
        try {
            await refreshPromise;
        } catch {
            return new Response(null, { status: 401 });
        }
    }

    const response = await fetch(url, { ...options, headers: buildHeaders() });

    if (response.status === 401) {
        // Reactive refresh: attempt once on 401, then retry the original request
        if (!refreshPromise) {
            refreshPromise = refreshSession().finally(() => { refreshPromise = null; });
        }
        try {
            await refreshPromise;
        } catch {
            return response;
        }
        return fetch(url, { ...options, headers: buildHeaders() });
    }

    return response;
}
